// @ts-check
import React, { useState } from "react";

import { SubmitState } from "consts";
import { actions } from "actions";
import { connect } from "unistore/preact";
import { MIN_AGE, MAX_AGE, clamp, findOptionForAge, getInitialAgeFromOptions } from "utils/age";

/**
 * @template T
 * @typedef {import('preact').ComponentType<T>} ComponentType
 */

/**
 * @typedef {import('preact').JSX.Element} JSXElement
 * @typedef {import('preact').JSX.TargetedEvent<HTMLInputElement>} InputEvent
 */

const INITIAL_SLIDER_POSITION = Math.round((MIN_AGE + MAX_AGE) / 2);
const TICK_VALUES = [13, 20, 30, 40, 50, 60, 70, 80, 90];
// Tick label inset from track edges. Splits the difference between thumb-center
// alignment (16px = thumb radius) and track-edge alignment (0px) for visual balance.
const TICK_INSET_PX = 8;

/**
 * @typedef {import('types/api-responses').QuestionOption} QuestionOption
 *
 * @typedef {Object} AgeSliderQuestionProps
 * @property {import('types/api-responses').QuestionRadio} question
 * @property {string} labelId
 * @property {boolean} [isDisabled]
 * @property {(args: { option: QuestionOption, autoAdvance: boolean }) => void} onOptionChanged
 * @property {(submitState: number) => void} setSubmitState
 */

/**
 * @param {AgeSliderQuestionProps} props
 * @returns {JSXElement}
 */
const AgeSliderQuestion = ({ question, labelId, isDisabled = false, onOptionChanged, setSubmitState }) => {
  const initialAge = getInitialAgeFromOptions(question.options);
  const [age, setAge] = useState(/** @type {number | null} */ (initialAge));
  const [inputValue, setInputValue] = useState(initialAge !== null ? String(initialAge) : "");

  /** @param {number} newAge */
  const commitAge = (newAge) => {
    setAge(newAge);
    setInputValue(String(newAge));
    const option = findOptionForAge(newAge, question.options);
    if (option) {
      onOptionChanged({ option, autoAdvance: false });
      setSubmitState(SubmitState.Ready);
    }
  };

  /** @param {InputEvent} e */
  const handleInputChange = (e) => {
    const raw = e.currentTarget.value;
    setInputValue(raw);
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed) && parsed >= MIN_AGE && parsed <= MAX_AGE) {
      setAge(parsed);
      const option = findOptionForAge(parsed, question.options);
      if (option) {
        onOptionChanged({ option, autoAdvance: false });
      }
    }
  };

  const handleInputBlur = () => {
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || inputValue.trim() === "") {
      setInputValue(age !== null ? String(age) : "");
    } else {
      commitAge(clamp(parsed));
    }
  };

  /** @param {InputEvent} e */
  const handleSliderChange = (e) => {
    commitAge(parseInt(e.currentTarget.value, 10));
  };

  const sliderValue = age ?? INITIAL_SLIDER_POSITION;

  const handleDecrement = () => {
    if (sliderValue > MIN_AGE) {
      commitAge(sliderValue - 1);
    }
  };

  const handleIncrement = () => {
    if (sliderValue < MAX_AGE) {
      commitAge(sliderValue + 1);
    }
  };

  const atMinimum = sliderValue <= MIN_AGE;
  const atMaximum = sliderValue >= MAX_AGE;

  return (
    <div className="csw-age-slider-question" aria-labelledby={labelId}>
      <div className="csw-age-slider-controls">
        <button
          type="button"
          className="csw-age-slider-btn outline"
          aria-label="Decrease age"
          onClick={handleDecrement}
          disabled={isDisabled || atMinimum}
        >
          −
        </button>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={3}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          className="csw-age-slider-num outline"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={isDisabled}
          aria-labelledby={labelId}
        />
        <button
          type="button"
          className="csw-age-slider-btn outline"
          aria-label="Increase age"
          onClick={handleIncrement}
          disabled={isDisabled || atMaximum}
        >
          +
        </button>
      </div>
      <div className="csw-age-slider-track-wrapper">
        <input
          type="range"
          className="csw-age-slider-range"
          min={MIN_AGE}
          max={MAX_AGE}
          step={1}
          value={sliderValue}
          onChange={handleSliderChange}
          disabled={isDisabled}
          aria-labelledby={labelId}
          aria-valuemin={MIN_AGE}
          aria-valuemax={MAX_AGE}
          aria-valuenow={sliderValue}
          aria-valuetext={age === null ? "Not set" : String(age)}
        />
        <div className="csw-age-slider-ticks" aria-hidden="true">
          {TICK_VALUES.map((val) => {
            const pct = (val - MIN_AGE) / (MAX_AGE - MIN_AGE);
            return (
              <span
                key={val}
                className="csw-age-slider-tick"
                style={{ left: `calc(${TICK_INSET_PX}px + ${pct} * (100% - ${TICK_INSET_PX * 2}px))` }}
              >
                {val}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/** @type {ComponentType<AgeSliderQuestionProps>} */
export default connect("", actions)(AgeSliderQuestion);
